import { Modal } from '@cluey/cluey-components';
import { useEffect, useState } from 'react';
import { generatePath, useHistory } from 'react-router';

import { api } from '../../../../../api';
import { Enrolment } from '../../../../../api/types/enrolment';
import { Student } from '../../../../../api/types/student';
import EnrolmentLayout from '../../../../../common/hub/EnrolmentLayout';
import { OptionCard, OptionCardContent } from '../../../../../components/EnrolmentManagement/OptionCard';
import { IntentIntro } from '../../../../../components/IntentIntro';
import { PlusIcon } from '../../../../../icons';
import { ELEMENT_TYPES } from '../../../../../types/hubEnums';
import { AUSTRALIA_STATES } from '../../../../../util/constants';
import { PATH_HUB_ADD_STUDENT, PATH_HUB_CR_ADD_ENROLMENT } from '../../../../../util/pagePath';
import { PATH_HUB_ADD_ONE_OFF_SESSION } from '../../../../../util/pagePath';
import { usePageHeader } from '../../../../../v2/app-shell/usePageHeader';

const ManageServiceStudentsAndSubjects = () => {
  const { setHeader } = usePageHeader();

  const history = useHistory();
  const [modalVisible, setModalVisible] = useState(false);
  const { data: students, isLoading: loadingStudents } = api.student.all.useQuery();
  const { data: enrolments, isLoading: loadingEnrolments } = api.enrolment.all.useQuery('active');

  useEffect(() => {
    setHeader({
      label: 'Add a subject',
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader]);

  const modalConfig = {
    choices: enrolments ?? [],
    title: 'Select enrolment',
    subtitle: '',
    callback: (choice: Enrolment) => {
      const { enrolmentId } = choice;
      history.push(generatePath(PATH_HUB_ADD_ONE_OFF_SESSION, { enrolmentId }));
    },
  };

  const onStudentOptionClick = (student: Student) => {
    const state = Object.entries(AUSTRALIA_STATES).find(([key]) => key === student.location);
    history.push(PATH_HUB_CR_ADD_ENROLMENT, {
      firstName: student.firstName,
      lastName: student.lastName,
      yearLevel: student.yearLevel ? student.yearLevel : undefined,
      location: state ? state[1] : undefined,
      studentId: student.studentId,
    });
  };

  const onAddNewStudent = () => {
    history.push(PATH_HUB_ADD_STUDENT, {
      enrolStudent: true,
    });
  };

  return (
    <EnrolmentLayout
      sectionHeading="Home"
      mainHeading="Enrol into a subject"
      showEnrolmentStatus={false}
      showSummerModeEnrolmentStatus={false}
      isLoading={loadingStudents || loadingEnrolments}
    >
      <div className="flex min-h-screen flex-col px-5 pb-10 md:px-10">
        <div className="container mr-auto flex flex-col gap-y-5 px-0 pb-16 md:gap-y-10">
          <IntentIntro>Who would you like to enrol into a new learning program?</IntentIntro>
          <ul className="list-none">
            {students?.map((student) => (
              <li key={student.studentId}>
                <OptionCard onClickHandler={() => onStudentOptionClick(student)} type={ELEMENT_TYPES.BUTTON}>
                  <OptionCardContent>{student.firstName}</OptionCardContent>
                </OptionCard>
              </li>
            ))}
            <li key="add-a-new-student">
              <OptionCard onClickHandler={onAddNewStudent} type={ELEMENT_TYPES.BUTTON}>
                <OptionCardContent>
                  <div className="flex flex-row items-center gap-x-2">
                    <PlusIcon width={16} height={16} className="mr-2" />
                    Add a new student
                  </div>
                </OptionCardContent>
              </OptionCard>
            </li>
          </ul>
          <div>
            <IntentIntro>Already enrolled and want extra sessions?</IntentIntro>
            <button className="cursor-pointer text-blue-5" onClick={() => setModalVisible(true)}>
              Add ad-hoc sessions
            </button>
          </div>
        </div>
      </div>
      <Modal
        open={modalVisible}
        accent={false}
        setOpen={setModalVisible}
        closeOnBackground
        className="z-10 !w-11/12 !rounded-3xl md:!w-full"
        closeButton
      >
        <div className="px-2 py-4 md:px-12 md:py-14">
          <h2 className="pb-6 font-display text-2xl font-bold tracking-[-0.02rem] text-grey-600 md:text-[32px] md:leading-[38px]">
            {modalConfig.title}
          </h2>
          <IntentIntro className="mb-5 !text-base text-grey-600 md:mb-10 md:!text-lg">
            {modalConfig.subtitle || 'Which enrolment do you need to add ad-hoc sessions to?'}
          </IntentIntro>
          <ul className="list-none">
            {modalConfig.choices.map((choice, index) => (
              <li key={`${modalConfig.title}-${choice.student}-${index}`}>
                <OptionCard className="!bg-brown-100" onClickHandler={() => modalConfig.callback(choice)}>
                  <OptionCardContent>{`${choice.student} - ${choice.yearLevel} ${choice.subject} with ${choice.tutor}`}</OptionCardContent>
                </OptionCard>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </EnrolmentLayout>
  );
};

export default ManageServiceStudentsAndSubjects;
